/**
 * Registro único de operadores de filtro — fonte de verdade compartilhada
 * por `filterEngine` (avaliação cliente) e `serverFilterConditions`/`dealsSavedFilterQuery`
 * (push-down PostgREST). Garante que uma mesma regra produz o mesmo conjunto
 * de registros, seja avaliada no servidor, no cliente, ou em modo misto.
 */
import {
  startOfDay, endOfDay, startOfWeek, endOfWeek,
  startOfMonth, endOfMonth, startOfYear, endOfYear,
  startOfQuarter, endOfQuarter,
  subDays, subWeeks, subMonths, subYears, subQuarters,
  parseISO, isValid, isWithinInterval,
} from "date-fns";

import { NO_OWNER_SENTINEL } from "./ownerFilterQuery";
import { splitFilterValue, postgrestInList } from "./valueTokens";

export { NO_OWNER_SENTINEL };

export const RELATIVE_DATE_PRESETS = new Set<string>([
  "today", "yesterday",
  "this_week", "last_week",
  "this_month", "last_month",
  "this_quarter", "last_quarter",
  "this_year", "last_year",
]);

const DATE_RANGE_OPS = new Set<string>([
  "after", "before", "between",
  ...RELATIVE_DATE_PRESETS,
]);

export function isRelativeDatePreset(value: unknown): value is string {
  return typeof value === "string" && RELATIVE_DATE_PRESETS.has(value.trim());
}

/**
 * Espaços "invisíveis" → espaço comum, antes de qualquer comparação de texto.
 *
 * Formulários e webhooks gravam NBSP (U+00A0) no lugar do espaço: em produção
 * `companies.custom_data.faturamento_mensal` tem 1.675 empresas com
 * "Mais<NBSP>de<NBSP>1<NBSP>milhão" e 54 com espaço comum. Um filtro digitado à
 * mão ("Mais de 1 milhão") casava só com as 54.
 *
 * Espelha `public._filter_text_norm` no Postgres — o mesmo filtro avaliado aqui
 * ou pelo construtor SQL tem que devolver o MESMO conjunto de registros.
 */
const INVISIBLE_SPACES = new RegExp(
  `[${String.fromCharCode(0xa0)}${String.fromCharCode(0x202f)}${String.fromCharCode(0x2007)}]`,
  "g",
);

function normText(value: unknown): string {
  return String(value ?? "").replace(INVISIBLE_SPACES, " ");
}

/**
 * Divide o valor de uma regra em tokens limpos. Retorna `[]` quando vazio.
 *
 * Aceita os dois formatos (unit separator novo e CSV legado) — ver `valueTokens.ts`.
 * Opção de `select` pode conter vírgula, então o CSV cru não serve como formato único.
 */
function splitCsv(raw: string | null | undefined): string[] {
  const s = normText(raw).trim();
  if (!s) return [];
  return splitFilterValue(s);
}

/** Converte um preset relativo num intervalo absoluto (Date). */
export function relativeDateToRange(
  preset: string,
  now: Date = new Date()
): { from: Date; to: Date } | null {
  switch (preset) {
    case "today":
      return { from: startOfDay(now), to: endOfDay(now) };
    case "yesterday": {
      const d = subDays(now, 1);
      return { from: startOfDay(d), to: endOfDay(d) };
    }
    case "this_week":
      return { from: startOfWeek(now, { weekStartsOn: 1 }), to: endOfWeek(now, { weekStartsOn: 1 }) };
    case "last_week": {
      const d = subWeeks(now, 1);
      return { from: startOfWeek(d, { weekStartsOn: 1 }), to: endOfWeek(d, { weekStartsOn: 1 }) };
    }
    case "this_month":
      return { from: startOfMonth(now), to: endOfMonth(now) };
    case "last_month": {
      const d = subMonths(now, 1);
      return { from: startOfMonth(d), to: endOfMonth(d) };
    }
    case "this_quarter":
      return { from: startOfQuarter(now), to: endOfQuarter(now) };
    case "last_quarter": {
      const d = subQuarters(now, 1);
      return { from: startOfQuarter(d), to: endOfQuarter(d) };
    }
    case "this_year":
      return { from: startOfYear(now), to: endOfYear(now) };
    case "last_year": {
      const d = subYears(now, 1);
      return { from: startOfYear(d), to: endOfYear(d) };
    }
    default:
      return null;
  }
}

export const RELATION_MISSING = Symbol("relation_missing");

function parseDate(val: unknown): Date | null {
  if (val == null) return null;
  if (val instanceof Date) return isValid(val) ? val : null;
  if (typeof val !== "string") return null;
  const d = parseISO(val);
  return isValid(d) ? d : null;
}

function normDay(d: Date): number {
  return startOfDay(d).getTime();
}

/** Avaliação client-side de operador de data (campo data, presets relativos, intervalos). */
export function evaluateDateRule(
  fieldValue: unknown,
  operador: string,
  valor: string | null
): boolean {
  const v = (valor ?? "").trim();

  // Sintaxe legada: operador é o próprio preset (ex.: operador="this_month", valor="")
  if (RELATIVE_DATE_PRESETS.has(operador) && v === "") {
    const range = relativeDateToRange(operador);
    const d = parseDate(fieldValue);
    if (!range || !d) return false;
    return isWithinInterval(d, { start: range.from, end: range.to });
  }

  // Sintaxe nova: operador=equals/not_equals, valor=preset
  if (v && RELATIVE_DATE_PRESETS.has(v)) {
    const range = relativeDateToRange(v);
    const d = parseDate(fieldValue);
    if (!range) return operador === "not_equals";
    const inRange = d ? isWithinInterval(d, { start: range.from, end: range.to }) : false;
    if (operador === "equals") return inRange;
    if (operador === "not_equals") return !inRange;
  }

  switch (operador) {
    case "after": {
      const d = parseDate(fieldValue);
      const ref = parseDate(valor);
      return !!(d && ref && normDay(d) > normDay(ref));
    }
    case "before": {
      const d = parseDate(fieldValue);
      const ref = parseDate(valor);
      return !!(d && ref && normDay(d) < normDay(ref));
    }
    case "equals": {
      const d = parseDate(fieldValue);
      const ref = parseDate(valor);
      return !!(d && ref && normDay(d) === normDay(ref));
    }
    case "not_equals": {
      const d = parseDate(fieldValue);
      const ref = parseDate(valor);
      if (!d && !ref) return false;
      if (!d || !ref) return true;
      return normDay(d) !== normDay(ref);
    }
    case "between": {
      if (!v) return false;
      const parts = v.split("|");
      const from = parseDate(parts[0]);
      const to = parseDate(parts[1]);
      const d = parseDate(fieldValue);
      if (!d) return false;
      if (from && to) {
        const start = startOfDay(from);
        const end = endOfDay(to);
        if (start > end) return isWithinInterval(d, { start: end, end: start });
        return isWithinInterval(d, { start, end });
      }
      if (from) return normDay(d) >= normDay(from);
      if (to) return normDay(d) <= normDay(to);
      return false;
    }
    case "is_empty":
      if (fieldValue === RELATION_MISSING) return false;
      return !fieldValue;
    case "is_not_empty":
      if (fieldValue === RELATION_MISSING) return false;
      return !!fieldValue;
    default:
      return true;
  }
}

function matchesAnyTokenWithSentinels(fieldValue: unknown, parts: string[]): boolean {
  const isEmpty = !fieldValue || fieldValue === null || fieldValue === "";
  const str = normText(fieldValue);
  return parts.some((p) => (p === NO_OWNER_SENTINEL ? isEmpty : str === p));
}

/** Avaliação client-side de operador genérico (não-data). */
export function evaluateGenericRule(
  fieldValue: unknown,
  operador: string,
  valor: string | null
): boolean {
  switch (operador) {
    case "equals": {
      const parts = splitCsv(valor);
      if (parts.length === 0) return normText(fieldValue) === "";
      if (parts.length === 1) {
        const single = parts[0];
        if (single === NO_OWNER_SENTINEL) {
          return !fieldValue || fieldValue === "" || fieldValue === null;
        }
        return normText(fieldValue) === single;
      }
      return matchesAnyTokenWithSentinels(fieldValue, parts);
    }
    case "not_equals": {
      const parts = splitCsv(valor);
      if (parts.length === 0) return normText(fieldValue) !== "";
      if (parts.length === 1) {
        const single = parts[0];
        if (single === NO_OWNER_SENTINEL) {
          return !!fieldValue && fieldValue !== "" && fieldValue !== null;
        }
        return normText(fieldValue) !== single;
      }
      return !matchesAnyTokenWithSentinels(fieldValue, parts);
    }
    // `.trim()` no termo: o construtor SQL faz `btrim` no valor da regra, então um
    // espaço sobrando no filtro salvo não pode zerar o resultado só no cliente.
    case "contains":
      return normText(fieldValue).toLowerCase().includes(normText(valor).trim().toLowerCase());
    case "not_contains":
      return !normText(fieldValue).toLowerCase().includes(normText(valor).trim().toLowerCase());
    case "greater_than":
      return Number(fieldValue) > Number(valor);
    case "less_than":
      return Number(fieldValue) < Number(valor);
    case "greater_than_or_equal":
    case "greater_or_equal":
    case "gte":
      return Number(fieldValue) >= Number(valor);
    case "less_than_or_equal":
    case "less_or_equal":
    case "lte":
      return Number(fieldValue) <= Number(valor);
    case "between": {
      const sep = String(valor ?? "").includes("|") ? "|" : ",";
      const parts = String(valor ?? "").split(sep);
      const n = Number(fieldValue);
      return n >= Number(parts[0]) && n <= Number(parts[1]);
    }
    case "is_empty":
      if (fieldValue === RELATION_MISSING) return false;
      return !fieldValue || fieldValue === "" || fieldValue === null;
    case "is_not_empty":
      if (fieldValue === RELATION_MISSING) return false;
      return !!fieldValue && fieldValue !== "";
    default:
      return true;
  }
}

// ============================================================================
// Server-side push-down (PostgREST)
// ============================================================================

/**
 * Escapa metacaracteres LIKE/ILIKE em valores literais para `contains`/`not_contains`.
 * Sem isso, `%` ou `_` no termo de busca viram wildcards SQL silenciosamente.
 */
function escapeIlikeLiteral(s: string): string {
  return s.replace(/\\/g, "\\\\").replace(/%/g, "\\%").replace(/_/g, "\\_");
}

export type PushdownDecision =
  | { kind: "push"; apply: (q: any, field: string) => any }
  | { kind: "residual"; reason: string };

export interface PushdownContext {
  /** Se `true`, valor é preset relativo (ou operador é after/before/between) e o campo é uma coluna de data real. */
  isDateField: boolean;
  /** Cliente passa `true` quando o campo pertence à entidade primária e é coluna direta no Postgres. */
  isDirectColumn: boolean;
}

/**
 * Decide se uma regra `(operador, valor)` pode ser empurrada ao servidor.
 * - `{ kind: "push", apply }`: chame `apply(query, field)` para encadear a condição.
 * - `{ kind: "residual", reason }`: precisa de avaliação client-side em cima do conjunto retornado.
 *
 * **Pré-condição**: O caller já decidiu que `field` é coluna direta da tabela primária.
 * Regras em campos relacionados/`custom_data` SEMPRE viram residual.
 */
export function getServerPushdown(
  operador: string,
  valor: string | null,
  ctx: PushdownContext
): PushdownDecision {
  if (!ctx.isDirectColumn) {
    return { kind: "residual", reason: "non-direct-column" };
  }

  const raw = typeof valor === "string" ? valor.trim() : valor;
  const isRelPreset = isRelativeDatePreset(raw) || RELATIVE_DATE_PRESETS.has(operador);

  // Presets relativos só podem ir ao servidor se o campo for tipo data.
  if (isRelPreset && !ctx.isDateField) {
    return { kind: "residual", reason: "relative-date-on-non-date-field" };
  }

  // Conversão de preset relativo em intervalo (sempre que possível).
  if (isRelPreset && ctx.isDateField) {
    const preset = RELATIVE_DATE_PRESETS.has(operador) ? operador : String(raw);
    const range = relativeDateToRange(preset);
    if (!range) return { kind: "residual", reason: "unknown-relative-preset" };

    if (operador === "not_equals") {
      return {
        kind: "push",
        apply: (q, field) =>
          q.or(`${field}.lt.${range.from.toISOString()},${field}.gt.${range.to.toISOString()}`),
      };
    }
    // operador == equals OR operador é o próprio preset (legado)
    return {
      kind: "push",
      apply: (q, field) =>
        q.gte(field, range.from.toISOString()).lte(field, range.to.toISOString()),
    };
  }

  switch (operador) {
    case "equals": {
      const parts = splitCsv(raw as string);
      const hasSentinel = parts.includes(NO_OWNER_SENTINEL);
      const concrete = parts.filter((p) => p !== NO_OWNER_SENTINEL);

      // Mistura `__no_owner__` com ids → não dá pra fazer `is null OR in (...)` num único filtro PostgREST simples.
      if (hasSentinel && concrete.length > 0) {
        return { kind: "residual", reason: "no-owner-sentinel-mixed" };
      }
      if (hasSentinel) {
        return { kind: "push", apply: (q, field) => q.is(field, null) };
      }
      if (parts.length === 0) {
        return { kind: "push", apply: (q, field) => q.eq(field, "") };
      }
      if (parts.length === 1) {
        return { kind: "push", apply: (q, field) => q.eq(field, parts[0]) };
      }
      return { kind: "push", apply: (q, field) => q.in(field, parts) };
    }

    case "not_equals": {
      const parts = splitCsv(raw as string);
      const hasSentinel = parts.includes(NO_OWNER_SENTINEL);
      const concrete = parts.filter((p) => p !== NO_OWNER_SENTINEL);

      if (hasSentinel && concrete.length > 0) {
        return { kind: "residual", reason: "no-owner-sentinel-mixed" };
      }
      if (hasSentinel) {
        return { kind: "push", apply: (q, field) => q.not(field, "is", null) };
      }
      if (parts.length === 0) {
        return { kind: "push", apply: (q, field) => q.neq(field, "") };
      }
      if (parts.length === 1) {
        return { kind: "push", apply: (q, field) => q.neq(field, parts[0]) };
      }
      // `postgrestInList` cita cada token: o `.in()` do postgrest-js faz isso sozinho,
      // mas este `NOT IN` monta a lista à mão e um token com vírgula ou parêntese
      // ("Faz tratamento regularmente (terapia, médico e etc)") viraria vários valores.
      return { kind: "push", apply: (q, field) => q.not(field, "in", `(${postgrestInList(parts)})`) };
    }

    case "contains":
      return {
        kind: "push",
        apply: (q, field) => q.ilike(field, `%${escapeIlikeLiteral(String(raw ?? ""))}%`),
      };
    case "not_contains":
      return {
        kind: "push",
        apply: (q, field) => q.not(field, "ilike", `%${escapeIlikeLiteral(String(raw ?? ""))}%`),
      };

    case "greater_than":
      return { kind: "push", apply: (q, field) => q.gt(field, raw as string) };
    case "less_than":
      return { kind: "push", apply: (q, field) => q.lt(field, raw as string) };
    case "greater_than_or_equal":
    case "greater_or_equal":
    case "gte":
      return { kind: "push", apply: (q, field) => q.gte(field, raw as string) };
    case "less_than_or_equal":
    case "less_or_equal":
    case "lte":
      return { kind: "push", apply: (q, field) => q.lte(field, raw as string) };

    case "after":
      return { kind: "push", apply: (q, field) => q.gt(field, raw as string) };
    case "before":
      return { kind: "push", apply: (q, field) => q.lt(field, raw as string) };

    case "between": {
      const sep = String(raw ?? "").includes("|") ? "|" : ",";
      const [a, b] = String(raw ?? "").split(sep);
      if (!a || !b) return { kind: "residual", reason: "between-missing-bounds" };
      return { kind: "push", apply: (q, field) => q.gte(field, a).lte(field, b) };
    }

    case "is_empty":
      return { kind: "push", apply: (q, field) => q.is(field, null) };
    case "is_not_empty":
      return { kind: "push", apply: (q, field) => q.not(field, "is", null) };

    default:
      return { kind: "residual", reason: `unknown-operator:${operador}` };
  }
}
